import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef,
  ViewChildren,
  ViewEncapsulation,
  inject,
} from '@angular/core';

import {
  CasualTournamentEntity,
  PrizeDetails,
  TournamentsResponse,
} from '@fantaz/shared/data-access/models';
import { GameCardService } from '@fantaz/tournaments-v2/ui/game-card';

@Component({
  selector: 'fz-tournaments-cards-view',
  templateUrl: './tournaments-cards-view.component.html',
  styleUrls: ['./tournaments-cards-view.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fadeSlideInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('500ms', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('500ms', style({ opacity: 0, transform: 'translateY(20px)' })),
      ]),
    ]),
  ],
})
export class TournamentsCardsViewComponent {
  changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  #gameCardService: GameCardService = inject(GameCardService);

  @ViewChildren('game_card', { read: ElementRef })
  cardElements: QueryList<ElementRef>;

  @Input() tournaments!: TournamentsResponse[];

  @Input()
  filtersOpened = false;

  @Output()
  tournamentPressed = new EventEmitter<CasualTournamentEntity>();

  @Output()
  share = new EventEmitter<CasualTournamentEntity>();

  @Output()
  lastTournamentReached = new EventEmitter<string>();

  expanded: {
    template: TemplateRef<any>;
    source: MouseEvent;
    tournament: CasualTournamentEntity;
    mode?: 'fullscreen';
  } | null = null;

  isHovered: {
    template: TemplateRef<any>;
    source: MouseEvent;
    tournament: CasualTournamentEntity;
    mode?: 'fullscreen';
  } | null;

  getFirstPrize(tournament: CasualTournamentEntity) {
    if (!tournament.lifeExperience) {
      return null;
    }

    if (!tournament.eventSchedules) {
      return null;
    }

    const es = tournament.eventSchedules[0];

    if (!es) {
      return null;
    }

    const prices = [...es.prizeDetails].sort(
      (a: PrizeDetails, b: PrizeDetails) => Number(a.rank) - Number(b.rank),
    );

    if (prices.length === 0) {
      return null;
    }

    return prices[0];
  }

  getTitle(tournament: CasualTournamentEntity): string {
    const prize = this.getFirstPrize(tournament);

    if (prize) {
      return prize.prizeName as string;
    }

    if (tournament.lifeExperience) {
      const eventScheduleDetails = tournament.eventSchedules[0];
      if (eventScheduleDetails) {
        return eventScheduleDetails.prizeDetails[0]?.prizeDetails as string;
      }
    }
    return tournament.description;
  }

  getDescription(tournament: CasualTournamentEntity): string {
    const prize = this.getFirstPrize(tournament);

    if (prize) {
      return prize.prizeDetails as string;
    }

    if (tournament.lifeExperience) {
      const eventScheduleDetails = tournament.eventSchedules[0];
      if (eventScheduleDetails) {
        return eventScheduleDetails.prizeDetails[0]?.prizeDetails as string;
      }

      return tournament.metadata?.tournamentCardDescription as string;
    }
    return tournament.description;
  }

  trackByIdentifier(index: number, item: CasualTournamentEntity): string {
    return `card--item--${item.uuid}`;
  }

  onTournamentPress(tournament: CasualTournamentEntity) {
    this.tournamentPressed.emit(tournament);
  }

  onTournamentShare(tournament: CasualTournamentEntity) {
    this.share.emit(tournament);
  }

  isIntersecting(status: boolean, last: boolean) {
    if (status && last) {
      this.lastTournamentReached.emit();
    }
  }

  /**
   * Handles the mouse hover event on the game-tournaments component. Then calls
   * the onHoverDelay function to add a delay to the event.
   *
   * @param {MouseEvent} source - The source of the event.
   * @param {TemplateRef} template - The template associated with the event.
   * @param {CasualTournamentEntity} tournament - The tournament object associated with the event.
   */
  onMouseHover(
    source: MouseEvent,
    template: TemplateRef<any>,
    tournament: CasualTournamentEntity,
  ) {
    if (window.innerWidth >= 1024) {
      this.isHovered = {
        source,
        template,
        tournament,
      };
      this.onHoverDelay(source, template, tournament);
    }

    if (this.expanded?.tournament.uuid !== tournament.uuid) {
      this.expanded = null;
    }
  }

  /**
   * Handles the hover delay event. Validate is the mouse is still hovered over
   * the game-tournaments component.
   *
   * @param {MouseEvent} source - The source of the event.
   * @param {TemplateRef} template - The template associated with the event.
   * @param {CasualTournamentEntity} tournament - The tournament object associated with the event.
   */
  @debounce(200) onHoverDelay(
    source: MouseEvent,
    template: TemplateRef<any>,
    tournament: CasualTournamentEntity,
  ) {
    if (this.isHovered && this.isHovered.tournament.uuid === tournament.uuid) {
      this.expanded = {
        source,
        template,
        tournament,
      };
    } else {
      this.expanded = null;
    }

    this.changeDetectorRef.detectChanges();
  }

  onMouseLeave() {
    if (window.innerWidth >= 1024) {
      this.isHovered = null;
      this.expanded = null;
    }
  }

  onPreviewPress(
    tournament: CasualTournamentEntity,
    template: TemplateRef<any>,
    data?: {
      source: MouseEvent;
      template: TemplateRef<any>;
      tournament: CasualTournamentEntity;
    },
  ) {
    if (data) {
      this.#gameCardService.show({
        tournament,
        template,
      });
    } else {
      this.#gameCardService.close(tournament);
    }
  }
}

// function to add delay to a function
function debounce(wait: number) {
  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor,
  ) {
    const original = descriptor.value;

    descriptor.value = function (...args: any[]) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const context = this;
      setTimeout(() => {
        original.apply(context, args);
      }, wait);
    };

    return descriptor;
  };
}
